/**
 * Survey PREVIEW — staff see a survey exactly as a respondent would, drafts
 * included (whose public link is dead), and nothing is ever submitted.
 *
 * A bare `FullscreenDialog` (not `FullscreenFormDialog`: there is nothing to
 * save, so no Cancel / Save footer and no discard confirmation). The header
 * band is a persistent "Preview — answers aren't saved" banner with Close;
 * the body renders the SAME `SurveyRespondentShell` + `SurveyRespondentView`
 * as the public page (`/s/:token`). Submit runs the real client validation
 * (`SurveyForm`'s `buildSurveyAnswersSchema`, including the all-blank refusal)
 * and then shows the thank-you state — it never calls the API.
 *
 * The org theme is already applied inside the app, and this is a staff
 * surface, so no PostHog / Sentry replay suspension.
 *
 * Branding: the logo is the one a respondent will see — a saved survey's own
 * public snapshot (`surveyLogoUrl(public_logo_path)`), or, in the
 * builder before the first save, the org's CURRENT logo (what Create will
 * copy). The thank-you screen shows the survey's thank-you message and the
 * website button the respondent will get: the survey's own link, else the
 * org's website, worded by the shared `surveyWebsiteButton` rule
 * (`previewSurveyWebsiteButton`). The line above the title (`surveyIntroLine`:
 * the survey's own, else "{Project} · {Org}", else "A survey from {Org}") and
 * the layout (all questions, or one at a time) are the survey's own too.
 *
 * Mount conditionally (`{open && <SurveyPreviewDialog open … />}`).
 * `SurveyPreviewByIdDialog` loads the questions first (the index row has none).
 */
import { useCallback, useMemo, type FormEvent } from 'react';
import { Eye, X } from 'lucide-react';
import type { SurveyDisplayMode, SurveyQuestion } from '@releve/shared';
import { Button } from '@/components/ui/button';
import {
  FullscreenDialog,
  FullscreenDialogBody,
  FullscreenDialogContent,
  FullscreenDialogDescription,
  FullscreenDialogFooter,
  FullscreenDialogHeader,
  FullscreenDialogTitle,
} from '@/components/ui/fullscreen-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrganizationBusinessIdentity } from '@/hooks/useOrganizationBusinessIdentity';
import { useSurveyDetail } from '@/hooks/useSurveys';
import { useTranslation } from '@/hooks/useTranslation';
import { previewSurveyWebsiteButton, toSurveyPreviewContent } from './surveyPreviewContent';
import { SurveyRespondentShell, SurveyRespondentView } from './ui/SurveyRespondentView';

export interface SurveyPreviewContent {
  title: string;
  description?: string | null;
  questions: readonly SurveyQuestion[];
  thankYouMessage?: string | null;
  /** Shown above the eyebrow, as on the public page. */
  logoUrl?: string | null;
  /** The survey's own website override (raw); blank / NULL → the org's website. */
  websiteUrl?: string | null;
  /** The survey's own button text (raw); blank / NULL → "Visit {hostname}" / "Visit {org}". */
  websiteLabel?: string | null;
  /** The survey's own line above the title; blank / NULL → the default (`surveyIntroLine`). */
  introLine?: string | null;
  /** The survey's project name — feeds the default line ("{Project} · {Org}"). */
  projectName?: string | null;
  /** The layout the respondent gets; absent → every question on one page. */
  displayMode?: SurveyDisplayMode;
}

export interface SurveyPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What to show; `undefined` while it is still loading. */
  survey?: SurveyPreviewContent;
  isError?: boolean;
}

/** The preview "accepts" every valid response — and sends it nowhere. */
const acceptLocally = () => true;

/**
 * The dialog is portalled, but React bubbles synthetic events up the
 * COMPONENT tree: opened from the builder (inside `FullscreenFormDialog`'s
 * `<form>`), the preview's submit would otherwise also fire the builder's
 * onSubmit and SAVE the survey. Stop it at the preview.
 */
const stopSubmitBubbling = (event: FormEvent) => event.stopPropagation();

export function SurveyPreviewDialog({ open, onOpenChange, survey, isError = false }: SurveyPreviewDialogProps) {
  const { t } = useTranslation('surveys');
  const { selectedOrganization } = useOrganization();
  const orgName = selectedOrganization?.name ?? '';
  const { data: identity } = useOrganizationBusinessIdentity(selectedOrganization?.id);
  const websiteButton = previewSurveyWebsiteButton({
    override: survey?.websiteUrl,
    label: survey?.websiteLabel,
    orgWebsite: identity?.website_url,
    orgName,
  });
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  let body;
  if (isError) {
    body = <p className="text-center text-sm text-muted-foreground">{t('preview.loadError')}</p>;
  } else if (!survey) {
    body = (
      <div className="space-y-4" aria-busy="true" aria-label={t('preview.loading')}>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  } else {
    body = (
      <SurveyRespondentView
        orgName={orgName}
        title={survey.title}
        description={survey.description}
        questions={survey.questions}
        logoUrl={survey.logoUrl}
        introLine={survey.introLine}
        projectName={survey.projectName}
        displayMode={survey.displayMode}
        thankYouMessage={survey.thankYouMessage}
        websiteButton={websiteButton}
        onSubmit={acceptLocally}
        thankYouBody={t('preview.thankYouBody')}
      />
    );
  }

  return (
    <FullscreenDialog open={open} onOpenChange={onOpenChange}>
      <FullscreenDialogContent>
        <FullscreenDialogHeader>
          <div className="flex min-w-0 items-center gap-3">
            <Eye className="h-5 w-5 shrink-0" aria-hidden="true" />
            <FullscreenDialogTitle className="truncate text-base font-medium">{t('preview.banner')}</FullscreenDialogTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={close}
            aria-label={t('preview.close')}
            title={t('preview.close')}
            className="h-8 w-8 shrink-0 text-dialog-header-foreground hover:bg-dialog-header-foreground/20 hover:text-dialog-header-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </FullscreenDialogHeader>
        <FullscreenDialogDescription className="sr-only">{t('preview.description')}</FullscreenDialogDescription>

        <FullscreenDialogBody className="p-0">
          <div className="flex min-h-full flex-col" onSubmit={stopSubmitBubbling}>
            <SurveyRespondentShell className="min-h-0 flex-1">{body}</SurveyRespondentShell>
          </div>
        </FullscreenDialogBody>

        {/* The header X is easy to miss — a plain Close at the bottom too. */}
        <FullscreenDialogFooter>
          <Button type="button" onClick={close}>
            {t('preview.closeButton')}
          </Button>
        </FullscreenDialogFooter>
      </FullscreenDialogContent>
    </FullscreenDialog>
  );
}

export interface SurveyPreviewByIdDialogProps {
  surveyId: string;
  onOpenChange: (open: boolean) => void;
}

/** Preview a saved survey by id — loads its detail (shared cache) on mount. */
export function SurveyPreviewByIdDialog({ surveyId, onOpenChange }: SurveyPreviewByIdDialogProps) {
  const detail = useSurveyDetail(surveyId);
  const saved = detail.data?.survey;
  const survey = useMemo<SurveyPreviewContent | undefined>(() => (saved ? toSurveyPreviewContent(saved) : undefined), [saved]);
  return (
    <SurveyPreviewDialog
      open
      onOpenChange={onOpenChange}
      survey={survey}
      // A failed background refetch keeps the loaded survey on screen.
      isError={detail.data === null || (detail.isError && detail.data === undefined)}
    />
  );
}
